<script lang="ts">
	// The design system's Choice (design/components/core/Choice.jsx): picking
	// among options Segmented cannot hold, because there are more than four,
	// they carry a price, or several can be picked.
	//
	// single is a radio group, and tapping the picked option does nothing.
	// multi is checkboxes, and tapping a picked option unpicks it. At max the
	// unpicked options take the one disabled look but keep their price, so
	// "full" never reads as "sold out", which says so in words.
	//
	// Four states, four signals: picked is an ink edge and a filled mark, and
	// stays; pressed inverts the whole option for as long as the finger does;
	// disabled sinks into the well; resting is a hairline and an empty mark.
	// The mark is drawn in strokes of --text-strong, so it follows the press
	// and the disabled look without a second colour. Nothing reflows: the
	// height is fixed, the label clamped to two lines, and the edge is 1.5px
	// in every state.
	//
	// The requirement marker keeps its box whether met or not; unmet is
	// amber. It says what is missing and blocks nothing: the screen decides
	// what it disables.
	//
	// An option's sub is a second, quieter line (a saved address's area). Its
	// label then clamps to one line so the pair still fits the height; an
	// option without one keeps two lines, so a mixed group does not stagger.
	//
	// stack puts every option on its own full-width row, for a long list
	// picked by name where the label is the whole point (Edit my items): a
	// two-column grid cut a long dish name to half a word per line.
	//
	// The app adds test ids, and takes each price already formatted, sign
	// included: the design's delta flag is the caller's "+" here.

	import { pressable } from './pressable';

	interface Option {
		value: string;
		label: string;
		/** A second, quieter line under the label. */
		sub?: string;
		/** Already formatted, Latin digits. Absent when there is nothing extra to pay. */
		price?: string;
		soldOut?: boolean;
		testId: string;
	}

	interface Props {
		options: Option[];
		/** The picked value in single mode, the picked values in multi. */
		value: string | string[];
		onChange: (value: string | string[]) => void;
		soldOutLabel?: string;
		title?: string;
		rule?: string;
		mode?: 'single' | 'multi';
		min?: number;
		max?: number;
		testId?: string;
		ruleTestId?: string;
		/** One option per full-width row. */
		stack?: boolean;
	}

	let {
		options,
		value,
		onChange,
		soldOutLabel = '',
		title,
		rule,
		mode = 'single',
		min,
		max,
		testId,
		ruleTestId,
		stack = false,
	}: Props = $props();

	const multi = $derived(mode === 'multi');
	const picked = $derived(
		multi
			? Array.isArray(value)
				? value
				: []
			: typeof value === 'string' && value !== ''
				? [value]
				: [],
	);
	const lo = $derived(min ?? (multi ? 0 : 1));
	const hi = $derived(max ?? (multi ? Infinity : 1));
	const ok = $derived(picked.length >= lo && picked.length <= hi);
	const full = $derived(multi && picked.length >= hi);

	function tap(option: string) {
		if (multi) {
			onChange(picked.includes(option) ? picked.filter((x) => x !== option) : [...picked, option]);
		} else if (!picked.includes(option)) {
			onChange(option);
		}
	}
</script>

<div class="choice" role={multi ? 'group' : 'radiogroup'} aria-label={title} data-testid={testId}>
	{#if title || rule}
		<div class="head">
			<p class="title">{title}</p>
			{#if rule}
				<span class="rule" class:met={ok} role="status" data-testid={ruleTestId}>
					{#if ok}
						<!-- The design's check, drawn heavier at this size, as there. -->
						<svg
							class="tick"
							viewBox="0 0 24 24"
							width="14"
							height="14"
							fill="none"
							stroke="currentColor"
							stroke-width="2.6"
							stroke-linecap="round"
							stroke-linejoin="round"
							aria-hidden="true"
						>
							<path d="M5 12.5l4.5 4.5L19 7.5" />
						</svg>
					{/if}
					{rule}
				</span>
			{/if}
		</div>
	{/if}
	<div class="options" class:stack>
		{#each options as option (option.value)}
			{@const on = picked.includes(option.value)}
			{@const off = !!option.soldOut || (full && !on)}
			<button
				type="button"
				class="option"
				class:on
				role={multi ? 'checkbox' : 'radio'}
				aria-checked={on}
				data-testid={option.testId}
				data-press="invert"
				disabled={off}
				onclick={() => tap(option.value)}
				{@attach pressable}
			>
				<span class="mark" class:square={multi} class:on aria-hidden="true">
					{#if on}
						{#if multi}
							<svg
								viewBox="0 0 24 24"
								width="16"
								height="16"
								fill="none"
								stroke="currentColor"
								stroke-width="3"
								stroke-linecap="round"
								stroke-linejoin="round"
							>
								<path d="M5 12.5l4.5 4.5L19 7.5" />
							</svg>
						{:else}
							<span class="dot"></span>
						{/if}
					{/if}
				</span>
				<span class="text">
					<span class="label" class:one={!!option.sub}>{option.label}</span>
					{#if option.sub}
						<span class="sub">{option.sub}</span>
					{/if}
				</span>
				{#if option.soldOut}
					<span class="price words">{soldOutLabel}</span>
				{:else if option.price}
					<span class="price"><bdi dir="ltr">{option.price}</bdi></span>
				{/if}
			</button>
		{/each}
	</div>
</div>

<style>
	.choice {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		min-block-size: 28px;
	}

	.title {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	/* The same box met or unmet, so meeting it never shifts the heading. */
	.rule {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex: none;
		white-space: nowrap;
		font-size: 13px;
		font-weight: 700;
		padding: var(--chip-pad);
		border-radius: var(--radius-chip);
		border: var(--border-width) solid var(--border-flag);
		background: var(--surface-flag);
		color: var(--ink-flag-deep);
	}

	.rule.met {
		background: transparent;
		color: var(--text-muted);
		border-color: transparent;
	}

	.tick {
		display: block;
		flex: none;
		color: var(--accent-visibility);
	}

	.options {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--choice-min), 1fr));
		gap: var(--touch-gap);
	}

	.options.stack {
		grid-template-columns: minmax(0, 1fr);
	}

	.option {
		display: flex;
		align-items: center;
		gap: 10px;
		block-size: var(--choice-h);
		min-inline-size: 0;
		padding: 0 14px;
		text-align: start;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width-strong) solid var(--border-default);
		border-radius: var(--radius-tile);
		font-family: var(--font-sans);
		cursor: pointer;
	}

	.option.on {
		border-color: var(--text-strong);
	}

	.mark {
		flex: none;
		inline-size: 22px;
		block-size: 22px;
		box-sizing: border-box;
		display: grid;
		place-items: center;
		border: 2px solid var(--text-muted);
		border-radius: var(--radius-pill);
		color: var(--text-strong);
	}

	.mark.square {
		border-radius: 5px;
	}

	.mark.on {
		border-color: var(--text-strong);
	}

	.mark svg {
		display: block;
	}

	.dot {
		inline-size: 10px;
		block-size: 10px;
		border-radius: var(--radius-pill);
		background: var(--text-strong);
	}

	.text {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.label {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
		font-size: var(--text-xs);
		font-weight: 600;
		line-height: 1.35;
		color: var(--text-strong);
	}

	.label.one {
		-webkit-line-clamp: 1;
		line-clamp: 1;
	}

	.sub {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: var(--text-2xs);
		font-weight: 500;
		line-height: 1.3;
		color: var(--text-body);
	}

	.price {
		flex: none;
		font-size: 14px;
		font-weight: 600;
		color: var(--text-body);
		font-family: var(--font-mono);
		white-space: nowrap;
	}

	.price.words {
		font-family: var(--font-sans);
	}
</style>
